import { useCallback, type ReactNode } from 'react'

import RichText from '@/components/ui/rich-text'
import Link from '@/components/link-component'
import ImageContentful from '@/components/ui/image-contentful'
import IconFont from '@/components/ui/icon-font'
import CtaLink from './cta-link'

import { cx } from '@/utils/strings'
import { TypeComponentQuote } from '@/types/ctf/TypeComponentQuote'
import { useInspectorProps } from '@/utils/live-preview'
import { isPrint } from '@/utils/print'

type QuoteProps = TypeComponentQuote<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  entryId?: string
  className?: string
}

export default function Quote({
  entryId,
  actions,
  attribution,
  attributorTitle,
  body,
  image,
  quoteImage,
  link,
  className,
  stylePlain,
  background,
  fullBleedImageAsABackground,
  icon,
}: QuoteProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })
  const isPrintView = isPrint()

  // When the image is used as a full-bleed cover (portrait, text overlaid), resolve
  // the ImageContentful fields from either the plain `image` asset or `quoteImage`.
  const coverImageFields = fullBleedImageAsABackground
    ? (image?.fields ?? quoteImage?.fields?.image?.fields)
    : undefined
  const coverImageDarkMode =
    fullBleedImageAsABackground && !image?.fields ? quoteImage?.fields?.imageDarkMode?.fields : undefined

  const LinkWrap = useCallback(
    ({ children, to }: { children: ReactNode; to?: string }) => (
      <>
        {to ? (
          <Link className="w-full" to={to}>
            {children}
          </Link>
        ) : (
          children
        )}
      </>
    ),
    []
  )

  const renderImageAndAttribution = () => (
    <div
      className={cx(
        'flex gap-6 items-center',
        !fullBleedImageAsABackground && 'mb-5',
        !fullBleedImageAsABackground && !stylePlain && !isPrintView && '@min-2xl:mb-20'
      )}
    >
      {!fullBleedImageAsABackground && (quoteImage?.fields || image?.fields) && (
        <div className={cx('w-30 flex items-center justify-center ml-2')}>
          {quoteImage?.fields ? (
            <ImageContentful
              loading="eager"
              {...quoteImage.fields.image?.fields}
              darkModeImage={quoteImage.fields.imageDarkMode?.fields}
            />
          ) : (
            <ImageContentful loading="eager" {...image?.fields} />
          )}
        </div>
      )}
      {(attribution || attributorTitle) && (
        <div
          className={cx(
            ' [&_cite]:not-italic [&_cite]:block py-2',
            !fullBleedImageAsABackground &&
              (quoteImage?.fields || image?.fields) &&
              'pl-6 border-l-secondary-200 dark:border-l-secondary-500 border-l',
            !fullBleedImageAsABackground || isPrintView
              ? '@max-2xl:text-base @min-2xl:text-xl'
              : 'text-base [&_p]:text-base text-white'
          )}
        >
          {attribution && <cite className={cx(!fullBleedImageAsABackground && 'font-semibold')}>{attribution}</cite>}
          {attributorTitle && <p className="mb-0">{attributorTitle}</p>}
        </div>
      )}
    </div>
  )

  return (
    <LinkWrap to={link?.fields && link.fields.url}>
      <figure
        className={cx(
          'mb-1 @container',
          !stylePlain && ' border rounded-lg dark:bg-secondary-800',
          className,
          !background && !stylePlain && 'bg-white',
          background === 'Primary' && 'bg-primaryBlue text-white border-none',
          background === 'Secondary' && 'bg-bw-deep-blue text-white border-none',
          // Full-bleed cover image + a black scrim (bottom 0.7 → top 0.5) via ::after so
          // the quote stays legible. Sits above the bg layer (z-0) and below content (z-10).
          fullBleedImageAsABackground &&
            !isPrintView &&
            "aspect-6/7 lg:aspect-auto relative overflow-hidden after:content-[''] after:absolute after:inset-0 after:rounded-lg after:[background-image:linear-gradient(to_top,rgba(0,0,0,0.65),rgba(0,0,0,0.2))]"
        )}
        {...pageInspectorProps({ fieldId: 'body' })}
      >
        {coverImageFields && !isPrintView && (
          <div className="absolute inset-0 z-0">
            <ImageContentful
              loading="lazy"
              {...coverImageFields}
              darkModeImage={coverImageDarkMode}
              className="h-full w-full max-w-none! [&_picture]:block [&_picture]:h-full [&_img]:h-full! [&_img]:w-full [&_img]:object-cover!"
            />
          </div>
        )}
        <div
          className={cx(
            'z-10',
            !stylePlain && !isPrintView && '@max-2xl:p-6 @min-2xl:p-12',
            fullBleedImageAsABackground && !isPrintView ? 'absolute bottom-0 lg:relative lg:pt-52!' : 'relative',
            isPrintView && 'p-6'
          )}
        >
          {!fullBleedImageAsABackground && renderImageAndAttribution()}

          {icon?.fields?.unicode && (
            <div className="bg-primaryBlue h-[44px] w-[44px] md:h-[64px] md:w-[64px] flex items-center justify-center rounded mb-3 md:mb-5">
              <IconFont
                unicode={icon.fields.unicode}
                title={icon.fields.title}
                className={cx('block text-[22px]! md:text-[34px]! text-white')}
              />
            </div>
          )}

          <blockquote
            className={cx(
              !fullBleedImageAsABackground && 'inline-block [&_p]:text-base [&_p]:leading-8',
              !stylePlain &&
                !fullBleedImageAsABackground &&
                '@max-2xl:[&_p]:leading-7 @min-2xl:[&_p]:text-3xl @min-2xl:[&_p]:leading-9',
              fullBleedImageAsABackground &&
                !isPrintView &&
                '[&_p]:text-3xl [&_p]:leading-9 text-white [&_p]:font-medium [&_p]:mb-0'
            )}
          >
            <RichText body={body} />
          </blockquote>
          {fullBleedImageAsABackground && renderImageAndAttribution()}
          <div>
            {actions && (
              <div className="flex flex-row gap-6">
                {actions.map((action) => (
                  <CtaLink key={action?.sys.id} {...action} to={action.fields.link.fields.url}>
                    {action.fields.label}
                  </CtaLink>
                ))}
              </div>
            )}
          </div>
        </div>
      </figure>
    </LinkWrap>
  )
}
